"use client";

import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef } from "react";
import { plumeAPI } from "@/src/helpers/ServiceHelper";
import { useEditorContext } from "@/src/contexts/editorContext";
import { createUser, fetchBooksMetadataFromDb } from "@/src/helpers/dbFunctions";
import { setHasSessionCookie, clearHasSessionCookie } from "@/src/helpers/sessionCookie";
import { trackEvent, captureEvent } from "@/src/helpers/trackEvent";
import posthog from "posthog-js";
import { useRouter } from "next/navigation";
import { encodeBookId } from "@/src/helpers/utils";

const STORAGE = {
  REFERRAL_CODE: "referralCode",
  REFERRAL_SOURCE: "referralSource",
  ONBOARDING_STARTED: "onboardingStarted",
  USER_BOOTSTRAPPED: "userBootstrapped",
  SUBSCRIPTION_CANCEL_PENDING: "subscriptionCancelPending",
};

const WELCOME_BOOK_TITLE = "Welcome to Quarkle 💫";

function persistReferralFromUrlAndRedirectHome(router) {
  const params = new URLSearchParams(window.location.search);
  const referralCode = params.get("ref") || params.get("referral_code");

  if (referralCode) {
    localStorage.setItem(STORAGE.REFERRAL_CODE, referralCode);
    router.push("/");
  }
}

function captureReferrerSource() {
  if (!document.referrer) return null;
  try {
    const referrerUrl = new URL(document.referrer);
    const source = referrerUrl.hostname;
    localStorage.setItem(STORAGE.REFERRAL_SOURCE, source);
    return source;
  } catch (e) {
    console.error("Error parsing referrer URL:", e);
    return null;
  }
}

function clearStoredReferralParams() {
  localStorage.removeItem(STORAGE.REFERRAL_CODE);
  localStorage.removeItem(STORAGE.REFERRAL_SOURCE);
}

async function fetchAndStoreSubscription(token, editorDispatch, email) {
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const subscriptionResponse = await plumeAPI.get(`/subscriptions_api/subscriptions/fetch_one`, config);
  if (subscriptionResponse?.data) {
    const sub = subscriptionResponse.data;
    editorDispatch({ type: "subscription/set", payload: sub });
    posthog.identify(sub.user_id, {
      subscription: sub.subscription_type,
      email,
    });

    // Cancellation happens inside Stripe's hosted portal, which never calls
    // back into the app — this fetch (run on every auth bootstrap) is the
    // earliest point we can see the new state. Compare against the last-seen
    // value so this fires once per cancellation, not on every page load.
    const wasCancelling = localStorage.getItem(STORAGE.SUBSCRIPTION_CANCEL_PENDING) === "true";
    if (sub.stripe_cancel_at_period_end && !wasCancelling) {
      captureEvent("subscription_cancel_scheduled", { plan: sub.subscription_type });
    }
    localStorage.setItem(STORAGE.SUBSCRIPTION_CANCEL_PENDING, String(!!sub.stripe_cancel_at_period_end));
  }
}

function maybeRedirectToOnboardingBook(router, booksMetadata) {
  if (!Array.isArray(booksMetadata)) return;

  const hasStartedOnboarding = localStorage.getItem(STORAGE.ONBOARDING_STARTED) === "true";
  if (hasStartedOnboarding) return;

  if (booksMetadata.length === 1 && booksMetadata[0].title === WELCOME_BOOK_TITLE) {
    const welcomeBookId = encodeBookId(booksMetadata[0].id);
    router.push(`/editor/${welcomeBookId}`);
  }
}

export default function AuthRedirect({ children }) {
  const { isAuthenticated, isLoading, logout, getAccessTokenSilently, user } = useAuth0();
  const { dispatch: editorDispatch } = useEditorContext();
  const isBootstrappingRef = useRef(false);
  const router = useRouter();

  // Keep the qk_has_session cookie in sync with Auth0's resolved state so the
  // server component at app/page.jsx can render the right shell from byte one.
  // Only clear once Auth0 has fully resolved (isLoading=false) — otherwise the
  // initial mount would wipe the cookie before the session check completes.
  useEffect(() => {
    if (isAuthenticated) {
      setHasSessionCookie();
    } else if (!isLoading) {
      clearHasSessionCookie();
      // Reset the createUser gate so a subsequent login (potentially a different
      // account) re-runs the bootstrap path on the next authenticated mount.
      sessionStorage.removeItem(STORAGE.USER_BOOTSTRAPPED);
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    persistReferralFromUrlAndRedirectHome(router);
  }, [router]);

  useEffect(() => {
    if (!isAuthenticated || isBootstrappingRef.current) return;

    let cancelled = false;

    const bootstrap = async () => {
      isBootstrappingRef.current = true;
      editorDispatch({ type: "studio/loading/set", payload: true });

      try {
        // Resolve the access token once and share it across the bootstrap helpers.
        // Concurrent getAccessTokenSilently() calls can race for refresh-token rotation,
        // tripping Auth0's reuse detection and revoking the chain — which would cascade
        // into a forced logout right after a successful login.
        const token = await getAccessTokenSilently();
        const tokenFn = () => Promise.resolve(token);

        // createUser is idempotent on the backend, but the POST is wasteful on every
        // mount — gate it to once per browser session. The flag is per-tab, so a new
        // tab still triggers it (which is fine for cross-tab signup edge cases).
        if (sessionStorage.getItem(STORAGE.USER_BOOTSTRAPPED) !== "true") {
          const referralSource = captureReferrerSource();
          const referralCode = localStorage.getItem(STORAGE.REFERRAL_CODE);
          const createUserResponse = await createUser({
            getAccessTokenSilently: tokenFn,
            plumeAPI,
            referralCode,
            referralSource: referralSource || localStorage.getItem(STORAGE.REFERRAL_SOURCE),
          });
          if (referralCode) {
            captureEvent("referral_signup_submitted", {
              new_user: createUserResponse?.status === 201,
              code: referralCode,
            });
          }
          clearStoredReferralParams();
          sessionStorage.setItem(STORAGE.USER_BOOTSTRAPPED, "true");
          // 201 = brand new user row; 200 = existing user re-authenticating.
          // Gated by the same once-per-session flag as the createUser call
          // above, so this fires once per real login, not once per remount.
          captureEvent("login_completed", { new_user: createUserResponse?.status === 201 });
        }

        // Fetch subscription + books metadata in parallel before marking user ready.
        const [, metadataResult] = await Promise.allSettled([
          fetchAndStoreSubscription(token, editorDispatch, user?.email),
          fetchBooksMetadataFromDb({
            getAccessTokenSilently: tokenFn,
            isAuthenticated,
            logout,
            plumeAPI,
            editorDispatch,
          }),
        ]);

        if (metadataResult.status === "fulfilled") {
          maybeRedirectToOnboardingBook(router, metadataResult.value);
        }

        // Only mark user ready after the auth bootstrap is complete so editor
        // pages don't dispatch backend calls with a half-set-up auth context.
        if (!cancelled) {
          editorDispatch({ type: "user/ready/set", payload: true });
        }
      } catch (error) {
        editorDispatch({ type: "studio/loading/set", payload: false });
        // Unwrap the error so future "Seems like there's been some error" reports
        // surface a real cause (status, response body, error class) instead of a
        // generic Error object that doesn't serialize through analytics.
        const errorDetails = {
          name: error?.name,
          message: error?.message,
          status: error?.response?.status,
          data:
            typeof error?.response?.data === "string"
              ? error.response.data
              : JSON.stringify(error?.response?.data ?? null),
        };
        console.error("[AuthRedirect] Bootstrap failed:", errorDetails, error);
        if (error?.response?.status === 420) {
          editorDispatch({ type: "studio/error/set", payload: "google_login_needed" });
        } else {
          editorDispatch({ type: "studio/error/set", payload: "repeated" });
        }
        trackEvent("Signup Error", errorDetails);
        captureEvent("login_failed", errorDetails);
      } finally {
        isBootstrappingRef.current = false;
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getAccessTokenSilently, logout, editorDispatch, router, user?.email]);

  return <>{children}</>;
}
